import type { CSSProperties, FC, ReactNode, RefObject } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useHomeState } from '@/hooks/use-home-state'
import { PrimaryButton } from '@/components/generals/primary-button'
import { TextLink } from '@/components/generals/text-link'

import { HOME_SCENES } from '@/constants/scenes'

interface SceneCopyProps {
    contentRef: RefObject<HTMLDivElement | null>
}

type RevealPhase = 'pre' | 'in' | 'out'

interface DisplayState {
    scene: number
    phase: RevealPhase
}

/**
 * `--delay` paces scene changes; `--intro-delay` paces the opening draw-in from the start of its stage.
 */
const delay = (value: number, intro: number): CSSProperties => ({ '--delay': `${value}ms`, '--intro-delay': `${intro}ms` } as CSSProperties)

const INTRO_TAG = 100
const INTRO_LINE = 80

export const SceneCopy: FC<SceneCopyProps> = ({ contentRef }): ReactNode => {
    const state = useHomeState()
    const [display, setDisplay] = useState<DisplayState>({ scene: state.copyScene, phase: state.copyPhase })

    useEffect(() => {
        if (state.copyPhase === 'out') {
            setDisplay(current => ({ scene: current.scene, phase: 'out' }))

            return
        }

        let cancelled = false
        let raf = 0

        setDisplay(current => (current.scene === state.copyScene && current.phase === 'in') ? current : { scene: state.copyScene, phase: 'pre' })

        raf = requestAnimationFrame(() => {
            raf = requestAnimationFrame(() => {
                if (!cancelled) setDisplay({ scene: state.copyScene, phase: 'in' })
            })
        })

        return () => {
            cancelled = true
            cancelAnimationFrame(raf)
        }
    }, [state.copyScene, state.copyPhase])

    const scene = HOME_SCENES[display.scene]
    const entering = display.phase !== 'out'
    const last = scene.headline.length - 1
    const introBody = INTRO_TAG + (scene.headline.length + 1) * INTRO_LINE
    const introActions = introBody + 40

    return (
        <div ref={contentRef} data-reveal={display.phase} className='hero-copy max-w-full'>
            <p className='hud reveal-fade text-signal' style={delay(0, INTRO_TAG)}>
                {scene.tag}
            </p>

            <h1 id='scene-headline' className='display-xl mt-5 text-[clamp(34px,9.6vw,56px)] desk:mt-7 desk:whitespace-nowrap desk:text-[clamp(40px,3.5vw,76px)]'>
                {scene.headline.map((line, index) => (
                    <span key={`${display.scene}-${index}`} className='reveal-line'>
                        <span
                            className={cn('reveal-inner', index === last ? 'text-dim' : 'text-ink')}
                            style={delay(entering ? 120 + index * 90 : index * 40, INTRO_TAG + (index + 1) * INTRO_LINE)}
                        >
                            {line}
                        </span>
                    </span>
                ))}
            </h1>

            <p className='reveal-fade body-copy mt-6 text-ink/85' style={delay(380, introBody)}>
                {scene.body}
            </p>

            <div className='intro-actions reveal-fade mt-8 flex flex-wrap items-center gap-x-8 gap-y-5 desk:pointer-events-auto' style={delay(480, introActions)}>
                <PrimaryButton href='/contact' intro>
                    Engage the studio
                </PrimaryButton>

                <TextLink href={scene.link.href} intro>
                    {scene.link.label}
                </TextLink>
            </div>
        </div>
    )
}
